import {DataObjectId, DataObjectProperties} from './data-object'
import {Hub} from './hubs'
import {HubProperties} from './hubs'

export const groupChatContainerId =
  '00000000-9d40-46cf-8080-ecb809c35260' as DataObjectId
export const groupChatContainerName = '.group-chats'

/**
 * userId -> false and absence of userId are the same
 */
export interface GroupChatUsers {
  [userId: string]: boolean
}

export interface GroupChatUsersWithNotified {
  [userId: string]: {notified: boolean}
}

interface WithProps {
  properties?: DataObjectProperties
}

const strToUsers = (str: string): GroupChatUsers => {
  if (!str) {
    return {}
  }
  const res: GroupChatUsers = {}
  str.split('|').forEach(f => {
    const parts = f.split(':')
    if (parts[0]) {
      res[parts[0]] = parts[1] === '1' ? true : false
    }
  })
  return res
}

const usersToStr = (users: GroupChatUsers): string => {
  if (!users) {
    return ''
  }
  return Object.keys(users)
    .map(u => u + ':' + (users[u] ? '1' : '0'))
    .join('|')
}

export class GroupChat {
  public static create(
    companyId: DataObjectId,
    users: GroupChatUsers,
    title?: string
  ): Hub {
    const res = new Hub(companyId, '')
    res.location = groupChatContainerId
    res.title = res.id
    res.properties[HubProperties.groupChatUsers] = usersToStr(users)
    res.properties[HubProperties.groupChatNotified] = usersToStr(users)
    if (title) {
      res.properties[HubProperties.groupChatTitle] = title
    }
    return res
  }

  public static getTitle(hubLike: WithProps): string {
    if (!hubLike || !hubLike.properties) {
      return ''
    }
    return (hubLike.properties[HubProperties.groupChatTitle] as string) || ''
  }

  public static setTitleWithoutCommit(hubLike: WithProps, title: string): void {
    if (!hubLike) {
      return
    }
    if (!hubLike.properties) {
      hubLike.properties = {}
    }
    hubLike.properties[HubProperties.groupChatTitle] = title
  }

  public static setParticipantsWithoutCommit(
    hubLike: WithProps,
    users: GroupChatUsers
  ): void {
    if (!hubLike || !users) {
      return
    }
    if (!hubLike.properties) {
      hubLike.properties = {}
    }
    const oldUsers = strToUsers(
      hubLike.properties[HubProperties.groupChatUsers] as string
    )
    const added: DataObjectId[] = []
    const removed: DataObjectId[] = []
    Object.keys(oldUsers).forEach(uid => {
      if (!oldUsers[uid]) {
        return
      }
      if (!users[uid]) {
        removed.push(uid as DataObjectId)
      }
    })
    Object.keys(users).forEach(uid => {
      if (!users[uid]) {
        return
      }
      if (!oldUsers[uid]) {
        added.push(uid as DataObjectId)
      }
    })

    hubLike.properties[HubProperties.groupChatUsers] = usersToStr(users)

    const notified = strToUsers(
      hubLike.properties[HubProperties.groupChatNotified] as string
    )
    added.forEach(uid => {
      notified[uid] = true
    })
    removed.forEach(uid => {
      delete notified[uid]
    })
    hubLike.properties[HubProperties.groupChatNotified] = usersToStr(notified)
  }

  public static getParticipants(hubLike: WithProps): GroupChatUsers {
    if (!hubLike || !hubLike.properties) {
      return {}
    }
    return strToUsers(
      hubLike.properties[HubProperties.groupChatUsers] as string
    )
  }

  public static getParticipantsWithNotified(
    hubLike: WithProps
  ): GroupChatUsersWithNotified {
    if (!hubLike || !hubLike.properties) {
      return {}
    }
    const users = strToUsers(
      hubLike.properties[HubProperties.groupChatUsers] as string
    )
    const usersNotified = strToUsers(
      hubLike.properties[HubProperties.groupChatNotified] as string
    )

    const result: GroupChatUsersWithNotified = {}
    for (const userId of Object.keys(users)) {
      if (!users[userId]) {
        continue
      }
      result[userId] = {notified: !!usersNotified[userId]}
    }

    return result
  }

  /**
   * @deprecated
   * This method is slow and should not be used.
   * The bad performance impact is in cases when
   * there is a bunch of users and they are all
   * checked by this method in a loop
   */
  public static isNotified(
    hubLike: WithProps | undefined,
    userId: DataObjectId | undefined
  ): boolean {
    if (!userId || !hubLike || !hubLike.properties) {
      return false
    }
    const usersStr = hubLike.properties[HubProperties.groupChatUsers] as string
    const notyStr = hubLike.properties[
      HubProperties.groupChatNotified
    ] as string
    if (!usersStr || !notyStr) {
      return false
    }

    const users: GroupChatUsers = {}
    const notified: GroupChatUsers = {}

    usersStr.split('|').forEach(f => {
      const parts = f.split(':')
      if (parts[0]) {
        users[parts[0]] = parts[1] === '1' ? true : false
      }
    })

    notyStr.split('|').forEach(f => {
      const parts = f.split(':')
      if (parts[0]) {
        notified[parts[0]] = parts[1] === '1' ? true : false
      }
    })

    return !!users[userId] && !!notified[userId]
  }

  public static setNotifiedWithoutCommit(
    hubLike: WithProps,
    usersWithNotified: GroupChatUsersWithNotified
  ): void {
    if (!hubLike || !usersWithNotified) {
      return
    }
    if (!hubLike.properties) {
      hubLike.properties = {}
    }
    const notifiedUsers = strToUsers(
      hubLike.properties[HubProperties.groupChatNotified] as string
    )

    Object.entries(usersWithNotified).forEach(([userId, {notified}]) => {
      if (!!notifiedUsers[userId] === notified) {
        return
      }
      if (notified) {
        notifiedUsers[userId] = true
      } else {
        delete notifiedUsers[userId]
      }
    })
    hubLike.properties[HubProperties.groupChatNotified] =
      usersToStr(notifiedUsers)
  }

  public static toggleNotifiedWithoutCommit(
    hubLike: WithProps,
    userId: DataObjectId
  ): void {
    if (!hubLike || !userId) {
      return
    }
    if (!hubLike.properties) {
      hubLike.properties = {}
    }
    const notified = strToUsers(
      hubLike.properties[HubProperties.groupChatNotified] as string
    )
    if (notified[userId]) {
      delete notified[userId]
    } else {
      notified[userId] = true
    }
    hubLike.properties[HubProperties.groupChatNotified] = usersToStr(notified)
  }

  public static isGroupChat(hubLike: {location: DataObjectId}): boolean {
    return hubLike && hubLike.location === groupChatContainerId
  }
}
